'use client';

import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useId, useState } from 'react';
import styled from 'styled-components';
import Expander from '@nib-components/expander';
import FocusTrap from 'focus-trap-react';
import { focusOutlineWidth, copyLineHeight, colorLightest, colorWhite, copyFontSizeSmall, linkLoud, focusOutlineStyle, focusOutlineColor, token } from '@nib-components/theme';
import Overlay from '@nib-components/overlay';
import { CardSystemIcon, ChevronDownSystemIcon, CloseSystemIcon } from '@nib/icons';
import { Box, Columns, Column, Inline, Hidden, Padding, breakpoint, m, p, pl } from '@nib/layout';
import Link from '@nib-components/link';
import { UnstyledButton } from '@nib-components/button';
export const DrawerWrapper = styled.div`
  background: var(--drawerColorBg, ${colorWhite});
  height: 100%;
  right: 3.5rem;
  z-index: 11;
  position: fixed;
  top: 0;
  left: 0;
  overflow: auto;
  transition: transform 0.3s ease-in-out;
  transform: ${_ref => {
  let {
    isOpen
  } = _ref;
  return isOpen ? 'translateX(0%)' : 'translateX(-100%)';
}};

  ${breakpoint('xs', 'md')`
    right: 0;
  `}
`;
const ScreenReaderHiddenWhenClosed = styled.div`
  display: ${_ref2 => {
  let {
    isOpen
  } = _ref2;
  return isOpen ? 'block' : 'none';
}};
`;
export const InvisibleButton = styled(props => /*#__PURE__*/_jsx(UnstyledButton, {
  ...props,
  padding: 4
}))`
  color: var(--drawerColorFgClose);

  &:focus-visible {
    outline: ${focusOutlineWidth} ${focusOutlineStyle} ${token('drawer.color.border.focus.top', focusOutlineColor)};
    outline-offset: -${focusOutlineWidth};
  }
`;
const DrawerOverlay = styled(Overlay)`
  z-index: 9;
`;
export const HeaderLink = styled.a`
  color: var(--drawerColorFgTop, ${linkLoud});
  font-weight: bold;
  text-decoration: none;

  &:focus-visible {
    outline: ${focusOutlineWidth} ${focusOutlineStyle} ${token('drawer.color.border.focus.top', focusOutlineColor)};
  }
`;
export const DrawerHeader = styled(Columns)`
  background-color: var(--drawerColorBgTop, ${colorWhite});
  border-bottom: 1px solid var(--drawerColorBorderDivider, ${colorLightest});
  color: var(--drawerColorFgTop, ${linkLoud});
  flex-wrap: wrap-reverse;
  ${breakpoint('mini')`
    flex-wrap: nowrap;
  `}
  justify-content: end;
`;
const DrawerHeading = styled.div`
  font-weight: bold;
  line-height: 1;
`;
export const QuoteIcon = styled(CardSystemIcon)`
  vertical-align: sub;
`;
const ExpanderButton = styled(InvisibleButton)`
  &&& {
    display: flex;
    width: 100%;
    justify-content: space-between;
    align-items: center;
    cursor: pointer;
    border: none;
    background: none;
    color: var(--drawerColorFgMenu, ${linkLoud});
    ${p(4)};
    text-align: start;
    line-height: ${copyLineHeight};

    /* this fixes the bug, but is a band-aid fix */
    transform: none !important;
  }

  &:focus-visible {
    outline: ${focusOutlineWidth} ${focusOutlineStyle} ${token('drawer.color.border.focus', focusOutlineColor)};
    outline-offset: -${focusOutlineWidth};
  }
`;
// eslint-disable-next-line  @typescript-eslint/no-unused-vars
const Chevron = styled(_ref3 => {
  let {
    expanded,
    ...otherProps
  } = _ref3;
  return /*#__PURE__*/_jsx(ChevronDownSystemIcon, {
    ...otherProps
  });
})`
  ${_ref4 => {
  let {
    expanded
  } = _ref4;
  return expanded && `transform: rotate(180deg);`;
}};
  transition: transform 300ms cubic-bezier(0.55, 0.085, 0.68, 0.53);
  color: var(--drawerColorFgChevron, inherit);
`;
const ExpanderContent = styled.ul`
  ${m(0)};
  ${p(0)};
  list-style: none;
`;
export const ExpanderLink = styled(Link).attrs({
  underline: false
})`
  &&& {
    box-sizing: border-box;
    display: flex;
    width: 100%;
    ${p(4)};
    text-align: start;
    color: var(--drawerColorFgItem, inherit);

    &:hover {
      color: var(--drawerColorFgLinkHover);
    }

    &:active {
      color: var(--drawerColorFgLinkPressed);
    }
  }

  &:focus-visible {
    outline: ${focusOutlineWidth} ${focusOutlineStyle} ${token('drawer.color.border.focus', focusOutlineColor)};
    outline-offset: -${focusOutlineWidth};
  }
`;
export const ExpanderSubLink = styled(ExpanderLink)`
  ${pl(6)};
`;
ExpanderSubLink.displayName = 'ExpanderSubLink';
const RibbonLink = styled(Link).attrs({
  'data-testid': 'ribbon-link'
})`
  &&& {
    color: var(--drawerColorFgLink, inherit);
    font-size: ${copyFontSizeSmall};

    &:hover {
      color: var(--drawerColorFgLinkHover);
    }

    &:active {
      color: var(--drawerColorFgLinkPressed);
    }

    &:focus-visible {
      outline: ${focusOutlineWidth} ${focusOutlineStyle} ${token('drawer.color.border.focus', focusOutlineColor)};
    }
  }
`;
RibbonLink.displayName = 'RibbonLink';
export const DrawerExpander = _ref5 => {
  let {
    link,
    children
  } = _ref5;
  const [isExpanded, setExpanded] = useState(false);
  const Icon = link.icon;
  const expanderId = useId();
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs(ExpanderButton, {
      title: link.title,
      "aria-expanded": isExpanded,
      "aria-controls": expanderId,
      onClick: () => setExpanded(!isExpanded),
      children: [/*#__PURE__*/_jsxs(LineHeightZeroInline, {
        space: 4,
        verticalAlign: "center",
        children: [Icon ? /*#__PURE__*/_jsx(Icon, {
          size: "md"
        }) : '', /*#__PURE__*/_jsx(DrawerHeading, {
          children: link.title
        })]
      }), /*#__PURE__*/_jsx(Chevron, {
        expanded: isExpanded
      })]
    }), /*#__PURE__*/_jsx(Expander, {
      expanded: isExpanded,
      id: expanderId,
      "aria-label": link.title,
      children: /*#__PURE__*/_jsx(ExpanderContent, {
        children: children
      })
    })]
  });
};
DrawerExpander.displayName = 'DrawerExpander';
export const DrawerMenu = styled.ul`
  display: flex;
  flex-direction: column;
  ${m(0)};
  ${p(0)};
  list-style: none;
`;
export const MenuItem = styled.li`
  display: block;
  position: relative;

  &:not(:first-of-type) {
    border-block-start: 1px solid var(--drawerColorBorderDivider, ${colorLightest});
  }

  &:last-of-type {
    border-block-end: 1px solid var(--drawerColorBorderDivider, ${colorLightest});
  }
`;
export const MenuLink = styled.a`
  box-sizing: border-box;
  appearance: none;
  border: none;
  background: none;
  display: flex;
  align-items: center;
  width: 100%;
  position: relative;
  ${p(4)};
  text-decoration: none;
  color: var(--drawerColorFgMenu, ${linkLoud});
  font-weight: bold;

  &:focus-visible {
    outline: ${focusOutlineWidth} ${focusOutlineStyle} ${token('drawer.color.border.focus', focusOutlineColor)};
    outline-offset: -${focusOutlineWidth};
  }
`;
const LineHeightZeroInline = styled(Inline)`
  line-height: 0;
`;
const LineHeightOneInline = styled(Inline)`
  line-height: 1;
`;
const DrawerContent = _ref6 => {
  let {
    navLinks,
    ribbonLinks,
    navSlot,
    handleCloseDrawer,
    children
  } = _ref6;
  const handleClick = (event, onClickFn) => {
    handleCloseDrawer();
    onClickFn && onClickFn(event);
  };
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs(DrawerMenu, {
      children: [navLinks && navLinks.length > 0 && navLinks.filter(link => !link.hideInDrawer).map(link => {
        // eslint-disable-next-line  @typescript-eslint/no-unused-vars
        const {
          id,
          url,
          title,
          icon,
          positionRight,
          targetBlank,
          children,
          onClick,
          ...otherProps
        } = link;
        const Icon = icon;
        return link.children ? /*#__PURE__*/_jsx(MenuItem, {
          children: /*#__PURE__*/_jsxs(DrawerExpander, {
            link: link,
            children: [url && /*#__PURE__*/_jsx("li", {
              children: /*#__PURE__*/_jsx(ExpanderLink, {
                id: `${id}-mobile`,
                title: title,
                href: url,
                target: targetBlank ? '_blank' : '',
                rel: targetBlank ? 'noopener noreferrer' : '',
                onClick: e => handleClick(e, onClick),
                ...otherProps,
                children: title
              })
            }, `${id}-mobile`), children && children.map(child => {
              const {
                id,
                url,
                title,
                targetBlank,
                onClick,
                ...otherProps
              } = child;
              return /*#__PURE__*/_jsxs("li", {
                children: [/*#__PURE__*/_jsx(ExpanderLink, {
                  id: `${id}-mobile`,
                  title: title,
                  href: url,
                  target: targetBlank ? '_blank' : '',
                  rel: targetBlank ? 'noopener noreferrer' : '',
                  onClick: e => handleClick(e, onClick),
                  ...otherProps,
                  children: title
                }), child.children && /*#__PURE__*/_jsx(DrawerMenu, {
                  children: child.children && child.children.map(subChild => {
                    const {
                      id,
                      url,
                      title,
                      targetBlank,
                      onClick,
                      ...otherProps
                    } = subChild;
                    return /*#__PURE__*/_jsx("li", {
                      children: /*#__PURE__*/_jsx(ExpanderSubLink, {
                        id: `${id}-mobile`,
                        title: title,
                        href: url,
                        target: targetBlank ? '_blank' : '',
                        rel: targetBlank ? 'noopener noreferrer' : '',
                        onClick: e => handleClick(e, onClick),
                        ...otherProps,
                        children: /*#__PURE__*/_jsxs(Inline, {
                          verticalAlign: "center",
                          space: 2,
                          children: [/*#__PURE__*/_jsx(Hidden, {
                            screenReader: true,
                            children: /*#__PURE__*/_jsx(Box, {
                              children: "—"
                            })
                          }), /*#__PURE__*/_jsx("span", {
                            children: title
                          })]
                        })
                      })
                    }, `${id}-mobile`);
                  })
                })]
              }, `${id}-mobile`);
            })]
          })
        }, id) : /*#__PURE__*/_jsx(MenuItem, {
          children: /*#__PURE__*/_jsx(MenuLink, {
            id: `${id}-mobile`,
            title: title,
            href: url,
            target: targetBlank ? '_blank' : '',
            rel: targetBlank ? 'noopener noreferrer' : '',
            onClick: e => handleClick(e, onClick),
            ...otherProps,
            children: /*#__PURE__*/_jsxs(LineHeightZeroInline, {
              space: 4,
              verticalAlign: "center",
              children: [Icon ? /*#__PURE__*/_jsx(Icon, {
                size: "md"
              }) : '', /*#__PURE__*/_jsx(LineHeightOneInline, {
                children: /*#__PURE__*/_jsx("span", {
                  children: title
                })
              })]
            })
          }, `${id}-mobile`)
        }, id);
      }), navSlot &&
      // eslint-disable-next-line  @typescript-eslint/no-explicit-any
      /*#__PURE__*/
      React.cloneElement(navSlot, {
        isInDrawer: true
      })]
    }), ribbonLinks && ribbonLinks.length > 0 && /*#__PURE__*/_jsx(Padding, {
      all: 4,
      children: /*#__PURE__*/_jsx(Inline, {
        space: 3,
        children: ribbonLinks.map((link, i) => {
          const {
            id,
            url,
            title,
            onClick,
            ...otherProps
          } = link;
          return /*#__PURE__*/_jsx(RibbonLink, {
            id: `${id}-mobile`,
            href: url,
            title: title,
            onClick: e => handleClick(e, onClick),
            ...otherProps,
            children: title
          }, `item-${i}-${id}-mobile`);
        })
      })
    }), children && /*#__PURE__*/_jsx(Box, {
      padding: 4,
      children: children
    })]
  });
};
DrawerContent.displayName = 'DrawerContent';
const Drawer = _ref7 => {
  let {
    isOpen = false,
    setIsOpen,
    navLinks,
    ribbonLinks,
    hideQuoteButton,
    quoteButtonUrl,
    navSlot,
    children,
    ...otherProps
  } = _ref7;
  const defaultedQuoteUrl = quoteButtonUrl ? quoteButtonUrl : '/health-insurance/join/welcome';
  const handleCloseDrawer = () => {
    setIsOpen(false);
  };
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(DrawerOverlay, {
      visible: isOpen,
      role: "dialog",
      "aria-modal": isOpen,
      "aria-label": "Navigation"
    }), /*#__PURE__*/_jsx(FocusTrap, {
      active: isOpen,
      focusTrapOptions: {
        clickOutsideDeactivates: true,
        onDeactivate: () => setIsOpen(false)
      },
      children: /*#__PURE__*/_jsx(DrawerWrapper, {
        isOpen: isOpen,
        ...otherProps,
        "data-mesh-component": "DRAWER",
        role: "dialog",
        "aria-modal": "true",
        "aria-label": "Navigation",
        children: /*#__PURE__*/_jsxs(ScreenReaderHiddenWhenClosed, {
          isOpen: isOpen,
          "data-testid": "drawer-hidden-when-closed",
          children: [/*#__PURE__*/_jsxs(DrawerHeader, {
            children: [/*#__PURE__*/_jsx(Column, {
              children: /*#__PURE__*/_jsx(Box, {
                padding: 4,
                display: "flex",
                justifyContent: "center",
                children: !hideQuoteButton && /*#__PURE__*/_jsx(HeaderLink, {
                  id: "ga-nav-hi-quote-mobile",
                  href: defaultedQuoteUrl,
                  "aria-label": "Get a quote",
                  onClick: handleCloseDrawer,
                  children: /*#__PURE__*/_jsxs(Inline, {
                    space: 2,
                    align: "center",
                    children: [/*#__PURE__*/_jsx(QuoteIcon, {}), /*#__PURE__*/_jsx("span", {
                      children: "Get a quote"
                    })]
                  })
                })
              })
            }), /*#__PURE__*/_jsx(Column, {
              width: "content",
              children: /*#__PURE__*/_jsx(InvisibleButton, {
                onClick: handleCloseDrawer,
                "aria-label": "Close menu",
                children: /*#__PURE__*/_jsx(CloseSystemIcon, {})
              })
            })]
          }), /*#__PURE__*/_jsx(DrawerContent, {
            navLinks: navLinks,
            ribbonLinks: ribbonLinks,
            navSlot: navSlot,
            handleCloseDrawer: handleCloseDrawer,
            children: children
          })]
        })
      })
    })]
  });
};
Drawer.displayName = 'Drawer';
Drawer.DrawerWrapper = DrawerWrapper;
Drawer.DrawerMenu = DrawerMenu;
export default Drawer;